import React from 'react';
import useAudioPlayer from './useAudioPlayer';
import usePlayableAudioUrl from './usePlayableAudioUrl';
import {
  buildLyricItems,
  formatTime,
  getActiveIndex,
  lyricSyncUnits,
  normalizeTimedLyrics,
} from './lyricsUtils';
import './AudioPlayer.scss';

export default function AudioPlayer({ music, t }) {
  const player = useAudioPlayer();
  const playable = usePlayableAudioUrl(music?.audioUrl);
  const timedLyrics = React.useMemo(
    () => normalizeTimedLyrics(music?.lyricsTimestamp),
    [music?.lyricsTimestamp],
  );
  const items = React.useMemo(
    () => buildLyricItems(music?.lyrics, timedLyrics),
    [music?.lyrics, timedLyrics],
  );
  const syncUnits = React.useMemo(() => lyricSyncUnits(items), [items]);
  const activeIndex = getActiveIndex(
    timedLyrics,
    syncUnits,
    player.state.current,
    player.state.duration,
  );

  return (
    <div className="audio-player">
      <AudioCore audioRef={player.audioRef} src={playable.url} sync={player.sync} reset={player.reset} />
      <LyricBackdrop items={items} activeIndex={activeIndex} t={t} />
      <PlayerControls playable={playable} player={player} t={t} />
    </div>
  );
}

function AudioCore({ audioRef, src, sync, reset }) {
  return (
    <audio ref={audioRef} src={src} preload="metadata" onTimeUpdate={sync} onLoadedMetadata={sync} onPlay={sync} onPause={sync} onEnded={reset} />
  );
}

function LyricBackdrop({ items, activeIndex, t }) {
  const containerRef = React.useRef(null);
  const activeRef = React.useRef(null);
  React.useEffect(() => {
    centerActiveLyric(containerRef.current, activeRef.current);
  }, [activeIndex]);
  return (
    <div ref={containerRef} className="audio-player__lyrics" aria-label={copy(t, 'videoGeneration.mv.preview.lyricsBackdrop', 'Lyrics')}>
      {items.map((item, index) => (
        <LyricItem key={`${item.type}-${item.text || index}-${index}`} item={item} activeIndex={activeIndex} activeRef={activeRef} />
      ))}
    </div>
  );
}

function LyricItem({ item, activeIndex, activeRef }) {
  if (item.type === 'spacer') return <span className="audio-player__spacer" aria-hidden="true" />;
  const isActive = item.lyricIndex === activeIndex;
  const className = `audio-player__line ${item.type === 'title' ? 'title' : ''} ${isActive ? 'active' : ''}`;
  return <p ref={isActive ? activeRef : null} className={className}>{item.text}</p>;
}

function PlayerControls({ playable, player, t }) {
  const { state } = player;
  const disabled = !playable.url || playable.loading;
  return (
    <div className="audio-player__controls">
      <PlayButton disabled={disabled} playing={state.playing} onClick={player.toggle} t={t} />
      <span>{formatTime(state.current)}</span>
      <Progress state={state} disabled={disabled} onSeek={player.seek} />
      <span>{formatTime(state.duration)}</span>
      {playable.error && <span className="audio-player__error">{copy(t, 'videoGeneration.mv.actions.audioLoadFailed', 'Audio failed to load')}</span>}
      {playable.loading && <span className="audio-player__status">{copy(t, 'videoGeneration.mv.actions.audioLoading', 'Loading audio')}</span>}
      <Volume value={state.volume} onChange={player.changeVolume} t={t} />
    </div>
  );
}

function PlayButton({ disabled, playing, onClick, t }) {
  return (
    <button className="audio-player__play" disabled={disabled} onClick={onClick} aria-label={playing ? copy(t, 'videoGeneration.mv.actions.pause', 'Pause') : copy(t, 'videoGeneration.mv.actions.play', 'Play')}>
      <span className={playing ? 'pause' : 'play'} />
    </button>
  );
}

function Progress({ state, disabled, onSeek }) {
  const max = Math.max(1, state.duration);
  const value = Math.min(state.current, state.duration || 0);
  return <input type="range" min="0" max={max} value={value} onChange={(event) => onSeek(Number(event.target.value))} disabled={disabled} />;
}

function Volume({ value, onChange, t }) {
  return (
    <label>
      <span>{copy(t, 'videoGeneration.mv.actions.volume', 'Volume')}</span>
      <input type="range" min="0" max="1" step="0.05" value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function centerActiveLyric(container, active) {
  if (!container || !active) return;
  const containerRect = container.getBoundingClientRect();
  const activeRect = active.getBoundingClientRect();
  const activeTop = activeRect.top - containerRect.top + container.scrollTop;
  const offset = activeTop - (container.clientHeight - activeRect.height) / 2;
  container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
}

function copy(t, key, fallback) {
  return typeof t === 'function' ? t(key) : fallback;
}
